import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/api';
import '../styles/AvvisoDetailPage.css';
import { usePermissions } from '../hooks/usePermissions';

const AvvisoDetailPage = () => {
  const { avviso_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isGuest } = usePermissions();
  const [avviso, setAvviso] = useState(null);
  const [loading, setLoading] = useState(true);
  const isGuestUser = isGuest();
  
  // Verifica se proviene dalla pagina notifiche o storico gare
  const fromNotifiche = searchParams.get('from') === 'notifiche';
  const fromStoricoGare = searchParams.get('from') === 'storico-gare';
  const configId = searchParams.get('config_id');
  const date = searchParams.get('date');
  
  const handleBack = () => {
    if (fromNotifiche && configId && date) {
      // Torna alla pagina notifiche con i parametri
      navigate(`/notifiche?config_id=${configId}&date=${date}`);
    } else if (fromStoricoGare) {
      // Torna allo storico gare con i filtri
      // Costruisci i parametri URL dai parametri esistenti (escludendo from, config_id, date)
      const params = new URLSearchParams();
      Array.from(searchParams.entries()).forEach(([key, value]) => {
        if (key !== 'from' && key !== 'config_id' && key !== 'date') {
          params.set(key, value);
        }
      });
      const queryString = params.toString();
      navigate(`/storico-gare${queryString ? '?' + queryString : ''}`);
    } else {
      // Torna allo storico gare senza filtri
      navigate('/storico-gare');
    }
  };

  useEffect(() => {
    fetchAvvisoDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avviso_id]);

  const fetchAvvisoDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        toast.error('Token non trovato. Effettua il login.');
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/storico-gare/${avviso_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📋 Dati avviso ricevuti:', response.data);
      console.log('📋 Sezione A:', response.data.sezione_a);
      console.log('📋 Sezione B:', response.data.sezione_b);
      setAvviso(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dettagli avviso:', error);
      if (error.response?.status === 404) {
        toast.error('Avviso non trovato');
        handleBack();
      } else if (error.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else {
        toast.error('Errore nel caricamento dettagli avviso');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/D';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('it-IT', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/D';
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/D';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="avviso-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Caricamento dettagli...</p>
        </div>
      </div>
    );
  }

  if (!avviso) {
    return (
      <div className="avviso-detail-page">
        <div className="error-container">
          <h2>Avviso non trovato</h2>
          <button onClick={handleBack} className="btn-back">
            ← Torna indietro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="avviso-detail-page">
      <div className="detail-header">
        <button onClick={handleBack} className="btn-back">
          ← {fromNotifiche ? 'Torna alle Notifiche' : fromStoricoGare ? 'Torna allo Storico' : 'Torna indietro'}
        </button>
        <h1>Dettagli Avviso</h1>
        <div className="avviso-meta">
          <span className={`status-badge status-${avviso.is_active ? 'active' : 'archived'}`}>
            {avviso.is_active ? '🟢 Attivo' : '📦 Archivato'}
          </span>
          {avviso.bucket && (
            <span className="bucket-badge">{avviso.bucket}</span>
          )}
        </div>
      </div>

      {/* SEZIONE A - Committente */}
      <div className="detail-section">
        <h2>SEZ. A - Committente</h2>
        <div className="section-content">
          {avviso.sezione_a?.fields?.soggetti_sa?.length > 0 ? (
            avviso.sezione_a.fields.soggetti_sa.map((soggetto, idx) => (
              <div key={idx} className="info-group">
                <div className="info-row">
                  <span className="info-label">Denominazione:</span>
                  <span className="info-value">{soggetto.denominazione_amministrazione || 'N/D'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Codice Fiscale:</span>
                  <span className="info-value">{soggetto.codice_fiscale || 'N/D'}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data">Nessun dato disponibile</div>
          )}
        </div>
      </div>

      {/* SEZIONE B - Dati Generali */}
      <div className="detail-section">
        <h2>SEZ. B - Dati Generali</h2>
        <div className="section-content">
          {avviso.sezione_b?.fields ? (
            <div className="info-group">
              {avviso.sezione_b.fields.documenti_di_gara_link && !isGuestUser && (
                <div className="info-row">
                  <span className="info-label">Link Documenti:</span>
                  <a 
                    href={avviso.sezione_b.fields.documenti_di_gara_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="link-external"
                  >
                    {avviso.sezione_b.fields.documenti_di_gara_link}
                  </a>
                </div>
              )}
              {avviso.codice_scheda && (
                <div className="info-row">
                  <span className="info-label">Codice Scheda:</span>
                  <span className="info-value">{avviso.codice_scheda}</span>
                </div>
              )}
              {avviso.codice_eform && (
                <div className="info-row">
                  <span className="info-label">Codice Eform:</span>
                  <span className="info-value">{avviso.codice_eform}</span>
                </div>
              )}
              {avviso.published_at && (
                <div className="info-row">
                  <span className="info-label">Data Pubblicazione:</span>
                  <span className="info-value">{formatDate(avviso.published_at)}</span>
                </div>
              )}
              {avviso.published_at_rettifica && (
                <div className="info-row">
                  <span className="info-label">Data Pubblicazione Rettifica:</span>
                  <span className="info-value">{formatDate(avviso.published_at_rettifica)}</span>
                </div>
              )}
              {(!avviso.sezione_b.fields.documenti_di_gara_link || isGuestUser) && !avviso.codice_scheda && !avviso.codice_eform && !avviso.published_at && (
                <div className="no-data">Nessun dato disponibile</div>
              )}
            </div>
          ) : (
            <div className="no-data">Nessun dato disponibile</div>
          )}
        </div>
      </div>

      {/* SEZIONE C - Oggetto (Lotti) */}
      <div className="detail-section">
        <h2>SEZ. C - Oggetto ({avviso.lotti.length} lotti)</h2>
        <div className="lotti-list">
          {avviso.lotti.length === 0 ? (
            <div className="no-data">Nessun lotto disponibile</div>
          ) : (
            avviso.lotti.map((lotto) => (
              <div key={lotto.id} className="lotto-detail-card">
                <div className="lotto-header">
                  <h3>{lotto.title || 'Lotto senza titolo'}</h3>
                  <span className={`status-badge status-${lotto.status}`}>
                    {lotto.status === 'active' ? '🟢 Attivo' : 
                     lotto.status === 'expired' ? '🔴 Scaduto' : 
                     lotto.status === 'archived' ? '📦 Archiviato' : 
                     '🟡 Aggiudicato'}
                  </span>
                </div>
                
                <div className="lotto-body">
                  {lotto.cig && (
                    <div className="info-row">
                      <span className="info-label">CIG:</span>
                      <span className="info-value">{lotto.cig}</span>
                    </div>
                  )}
                  
                  {lotto.description && (
                    <div className="info-row">
                      <span className="info-label">Descrizione:</span>
                      <span className="info-value">{lotto.description}</span>
                    </div>
                  )}
                  
                  {(lotto.cpv_raw || lotto.cpv_number) && (
                    <div className="info-row">
                      <span className="info-label">CPV:</span>
                      <span className="info-value">
                        {lotto.cpv_number ? `${lotto.cpv_number} - ` : ''}
                        {lotto.cpv_raw || 'N/D'}
                      </span>
                    </div>
                  )}
                  
                  {lotto.value && (
                    <div className="info-row">
                      <span className="info-label">Valore:</span>
                      <span className="info-value">{formatCurrency(lotto.value)}</span>
                    </div>
                  )}
                  
                  {lotto.value_estimated && (
                    <div className="info-row">
                      <span className="info-label">Valore Stimato:</span>
                      <span className="info-value">{formatCurrency(lotto.value_estimated)}</span>
                    </div>
                  )}
                  
                  {lotto.value_awarded && (
                    <div className="info-row">
                      <span className="info-label">Valore Aggiudicato:</span>
                      <span className="info-value">{formatCurrency(lotto.value_awarded)}</span>
                    </div>
                  )}
                  
                  {lotto.deadline && (
                    <div className="info-row">
                      <span className="info-label">Scadenza:</span>
                      <span className="info-value">{formatDate(lotto.deadline)}</span>
                    </div>
                  )}
                  
                  {lotto.deadline_offers && (
                    <div className="info-row">
                      <span className="info-label">Termine Ricezione Offerte:</span>
                      <span className="info-value">{formatDate(lotto.deadline_offers)}</span>
                    </div>
                  )}
                  
                  {lotto.deadline_invitation && (
                    <div className="info-row">
                      <span className="info-label">Termine Invito:</span>
                      <span className="info-value">{formatDate(lotto.deadline_invitation)}</span>
                    </div>
                  )}
                  
                  {(lotto.provincia || lotto.regione) && (
                    <div className="info-row">
                      <span className="info-label">Luogo:</span>
                      <span className="info-value">
                        {lotto.provincia && lotto.regione 
                          ? `${lotto.provincia} - ${lotto.regione}`
                          : lotto.provincia || lotto.regione}
                        {lotto.luogo_raw && ` (${lotto.luogo_raw})`}
                      </span>
                    </div>
                  )}
                  
                  {lotto.item_type && (
                    <div className="info-row">
                      <span className="info-label">Tipo Oggetto:</span>
                      <span className="info-value">{lotto.item_type}</span>
                    </div>
                  )}
                  
                  {lotto.is_award && (
                    <div className="info-row">
                      <span className="info-label">Aggiudicazione:</span>
                      <span className="info-value">✅ Sì</span>
                    </div>
                  )}
                  
                  <div className="info-row">
                    {isGuestUser ? (
                      <button
                        type="button"
                        className="btn-link btn-link--disabled"
                        title="L'utenza Guest consente solo l'accesso al motore di ricerca delle gare. Per utilizzare tutte le funzionalità è necessario associarsi"
                        disabled
                      >
                        📄 Link Documenti Gara
                      </button>
                    ) : (
                      <a 
                        href={lotto.source_url || '#'} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="btn-link"
                      >
                        📄 Link Documenti Gara
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AvvisoDetailPage;

